import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLoaderData } from '@remix-run/react';
import type { Feed } from '~/services/layout/index.ts';
import type { ServerLoadedFeeds } from '~/services/layout/server-loaded-feeds.ts';
import { requestJsonData } from '~/utils/request-json-data.ts';
import { getClientFeedUrl } from '~/services/request';
import type { loader as rootLoader } from '~/routes/_index.tsx';
import useCurrentLanguage from '~/hooks/use-current-language';
import { FormDataModel } from '../form-data-model.ts';
import { loadFormScreenComponentFeeds } from '../form-screen-component-feeds.ts';
import type { FormScreenStateStore } from '../form-screen-state.ts';
import {
  getFormValidationMessagesFromConfig,
  type FormValidationMessages,
} from '../form-validation-messages.ts';
import type {
  FormConfig,
  FormUiComponent,
  LayoutPresetsMapping,
} from '../types.ts';

type UseFormScreenModelInput = {
  stateStore: FormScreenStateStore;
};

type UseFormScreenModelResult = {
  formDataModel: FormDataModel | null;
  uiComponents: FormUiComponent[];
  serverLoadedFeeds: ServerLoadedFeeds;
  loadError: string | null;
};

/**
 * Builds a form data model from configuration.
 *
 * @param input - Form config, presets, screen id, state store, and remote loader.
 * @returns Initialized form data model.
 */
function createFormDataModel(input: {
  formConfig: FormConfig;
  layoutPresetsUsed: LayoutPresetsMapping;
  screenId: string;
  stateStore: FormScreenStateStore;
  loadRemoteFeed?: (url: string) => Promise<Feed>;
  validationMessages: FormValidationMessages;
}): FormDataModel {
  const model = new FormDataModel(
    input.layoutPresetsUsed,
    input.screenId,
    input.loadRemoteFeed,
    input.validationMessages
  );
  model.prepareFormDataFromConfig(input.formConfig, input.stateStore.getData());
  return model;
}

/**
 * Hydrates the form data model from loader data and keeps component feeds in sync.
 *
 * @param input - Active form screen state store.
 * @returns Form model state used by the form screen renderer.
 */
export function useFormScreenModel({
  stateStore,
}: UseFormScreenModelInput): UseFormScreenModelResult {
  const { screen, formScreenData, baseURL } =
    useLoaderData<typeof rootLoader>();
  const language = useCurrentLanguage();
  const validationMessages = useMemo(
    () => getFormValidationMessagesFromConfig({ screen, language }),
    [language, screen]
  );

  const loadRemoteFeed = useCallback(
    async (url: string): Promise<Feed> => {
      const clientFeedUrl = `${baseURL}/${getClientFeedUrl(url)}`;
      const feed = await requestJsonData({ url: clientFeedUrl });

      if (!feed) {
        throw new Error(`Failed to load remote feed from ${url}`);
      }

      return feed as Feed;
    },
    [baseURL]
  );

  const [uiComponents, setUiComponents] = useState<FormUiComponent[]>(
    () => formScreenData?.uiComponents ?? []
  );
  const [serverLoadedFeeds, setServerLoadedFeeds] = useState<ServerLoadedFeeds>(
    () => formScreenData?.serverLoadedFeeds ?? []
  );
  const loadError = formScreenData?.loadError ?? null;

  const formDataModel = useMemo(() => {
    if (!formScreenData?.formConfig) return null;

    const layoutPresetsUsed = formScreenData.layoutPresetsUsed;
    if (Object.keys(layoutPresetsUsed).length === 0) return null;

    return createFormDataModel({
      formConfig: formScreenData.formConfig,
      layoutPresetsUsed,
      screenId: screen.id,
      stateStore,
      loadRemoteFeed,
      validationMessages,
    });
  }, [
    formScreenData?.formConfig,
    formScreenData?.layoutPresetsUsed,
    loadRemoteFeed,
    screen.id,
    stateStore,
    validationMessages,
  ]);

  useEffect(() => {
    if (!formDataModel) return;

    let isActive = true;

    const unsubscribeComponents = formDataModel.subscribeComponents(
      async (components) => {
        if (!isActive) return;

        setUiComponents(components);

        const feeds = await loadFormScreenComponentFeeds(
          formDataModel,
          components
        );

        if (!isActive) return;

        setServerLoadedFeeds(feeds);
      }
    );

    return () => {
      isActive = false;
      unsubscribeComponents();
    };
  }, [formDataModel]);

  return {
    formDataModel,
    uiComponents,
    serverLoadedFeeds,
    loadError,
  };
}
